import { takeLatest, call, put } from 'redux-saga/effects';

import api from '../api';
import {
    listVehiclesRequest,
    listVehiclesSuccess,
    listVehiclesFailure,
    listVehiclesDeliveryRequest,
    listVehiclesDeliverySuccess,
    listVehiclesDeliveryFailure,
    getVehicleRequest,
    getVehicleSuccess,
    getVehicleFailure,
    updateVehicleRequest,
    updateVehicleSuccess,
    updateVehicleFailure,
    getMediaTypesRequest,
    getMediaTypesSuccess,
    getMediaTypesFailure,
    MediaType,
    Media,
    uploadMediaRequest,
    uploadMediaSuccess,
    uploadMediaFailure,
    uploadDocumentRequest,
    uploadDocumentSuccess,
    uploadDocumentFailure,
    Document,
    getInspectionsRequest,
    getInspectionsSuccess,
    getInspectionsFailure,
    InspectionSection,
    Vehicle,
    Store,
    Body,
    getStoreLocationsRequest,
    getStoreLocationsSuccess,
    getStoreLocationsFailure,
    getMakesRequest,
    getMakesSuccess,
    getMakesFailure,
    exportVehiclesRequest,
    updateMediaRequest,
    updateMediaSuccess,
    updateMediaFailure,
    startVehicleCheckoutRequest,
    getBodiesRequest,
    getBodiesSuccess,
    getBodiesFailure,
    GetMakesResponse,
    uploadBeforeAfterMediaRequest,
    uploadBeforeAfterMediaSuccess,
    uploadBeforeAfterMediaFailure,
    BeforeAfterMedia,
    genBookletRequest,
    getBookletCarfaxRequest,
    genBookletFactoryBaseConfigurationRequest,
    genBookletWhatWeDidRequest,
    genBookletNotesRequest,
    genBookletWhatHappenedRequest,
} from './actions';

// API calls
const listVehicleApi = async (filters: string, vehicle_fields: string, excludes: string, customlimit?: number): Promise<Vehicle[]> => {
    let params = new URLSearchParams({
        filters: filters || '',
        excludes: excludes || '',
        vehicle_fields: vehicle_fields,
        customlimit: customlimit?.toString() || ''
    }).toString();

    const response = await api.get('/api/vehicles/list/?' + params);
    return response.data;
};

const updateVehicleApi = async (vehicle: Vehicle, update_fields: string[], vehicle_fields?: string): Promise<Vehicle> => {
    let params = "";
    if (vehicle_fields) {
        params = "?"+ new URLSearchParams({
            vehicle_fields: vehicle_fields
        }).toString();
    };

    let data = [Object.fromEntries(Object.entries(vehicle).filter(([key]) => update_fields.includes(key)))];

    const response = await api.post('/api/vehicles/post/' + params, data);
    return response.data[0];
};

const getMediaTypesApi = async (): Promise<MediaType[]> => {
    const response = await api.get('/api/options/mediatypes/');
    return response.data;
};

const uploadMediaApi = async (payload: { vehicle_id: number; section_id: number; files: FileList; }): Promise<Media> => {
    const formData = new FormData();
    formData.append("id", payload.vehicle_id.toString());
    formData.append("section_id", payload.section_id.toString());
    Array.from(payload.files).forEach((file) => {
        formData.append("files", file);
    });

    const response = await api.post("/api/vehicles/upload-media/", formData, {
        headers: {
            "Content-Type": "multipart/form-data",
        },
    });
    return response.data;
};

const uploadDocumentApi = async (payload: {
    vehicle_id: number;
    files: FileList;
    filenames: string[];
    type: string;
}): Promise<Document> => {
    const formData = new FormData();
    formData.append("id", payload.vehicle_id.toString());
    formData.append("type", payload.type);
    payload.filenames.forEach((filename) => {
        formData.append("filenames", filename);
    });
    Array.from(payload.files).forEach((file) => {
        formData.append("files", file);
    });
    const response = await api.post("/api/vehicles/upload-document/", formData, {
        headers: {
            "Content-Type": "multipart/form-data",
        },
    });
    return response.data;
};

const getInspectionsApi = async (): Promise<InspectionSection[]> => {
    const response = await api.get('/api/options/inspections-sections/');
    return response.data;
};

const getStoreLocationsApi = async (): Promise<Store[]> => {
    const response = await api.get('/api/stores/list/');
    return response.data;
};

const getMakesApi = async (): Promise<GetMakesResponse> => {
    const response = await api.get('/api/options/makes/');
    return response.data;
};

const exportVehiclesApi = async (ids: number[]): Promise<Blob> => {
    const response = await api.get('/api/vehicles/export/', { params: { ids: ids.join(',') } });
    return response.data as Blob;
};

const startVehicleCheckoutApi = async (payload: {vehicle_id: number, type: string}): Promise<Vehicle> => {
    const response = await api.get("/api/vehicles/checkout-session/", {
        params: {
            id: payload.vehicle_id,
            type: payload.type,
        },
    });
    return response.data;
};

const updateMediaApi = async (payload: {vehicle_id: number, media: Media, update_fields: string[], media_fields?: string}): Promise<Media> => {
    let params = "";
    if (payload.media_fields) {
        params = "?"+ new URLSearchParams({
            media_fields: payload.media_fields
        }).toString();
    };

    let data = Object.fromEntries(Object.entries(payload.media).filter(([key]) => payload.update_fields.includes(key)));

    const response = await api.post('/api/vehicles/update-media/' + params, data);
    return response.data;
};

const getBodiesApi = async (): Promise<Body[]> => {
    const response = await api.get('/api/options/bodies/');
    return response.data;
};

const uploadBeforeAfterMediaApi = async (payload: {vehicle_id: number, before: FileList, after: FileList}): Promise<BeforeAfterMedia> => {
    const formData = new FormData();
    formData.append("id", payload.vehicle_id.toString());

    Array.from(payload.before).forEach((file) => {
        formData.append("before", file);
    });
    Array.from(payload.after).forEach((file) => {
        formData.append("after", file);
    });

    const response = await api.post("/api/vehicles/upload-before-after/", formData, {
        headers: {
            "Content-Type": "multipart/form-data",
        },
    });

    return response.data;
};

const genBookletSummaryApi = async (payload: {vehicle_id: number}): Promise<string> => {
    const response = await api.post("/api/vehicles/booklet-gen-summary/", {
        id: payload.vehicle_id
    });
    return response.data;
};

const getBookletCarfaxOverviewApi = async (payload: {vehicle_id: number}): Promise<string> => {
    const response = await api.post("/api/vehicles/booklet-gen-carfax/", {
        id: payload.vehicle_id,
    });
    return response.data;
};

const genBookletFactoryBaseConfigurationApi = async (payload: {vehicle_id: number}): Promise<string> => {
    const response = await api.post("/api/vehicles/booklet-gen-factory-config/", {
        id: payload.vehicle_id,
    });
    return response.data;
};

const genBookletWhatWeDidApi = async (payload: {vehicle_id: number}): Promise<string> => {
    const response = await api.post("/api/vehicles/booklet-gen-whatwedid/", {
        id: payload.vehicle_id,
    });
    return response.data;
};

const genBookletNotesApi = async (payload: {vehicle_id: number}): Promise<string> => {
    const response = await api.post("/api/vehicles/booklet-gen-notes/", {
        id: payload.vehicle_id,
    });
    return response.data;
};

const genBookletWhatHappenedApi = async (payload: {vehicle_id: number}): Promise<string> => {
    const response = await api.post("/api/vehicles/booklet-gen-whathappened/", {
        id: payload.vehicle_id,
    });
    return response.data;
};

// Sagas
function* handleListVehicle(action: ReturnType<typeof listVehiclesRequest>) {
    try {
        const data: Vehicle[] = yield call(
            listVehicleApi,
            action.payload.filters,
            action.payload.vehicle_fields,
            action.payload.excludes,
            action.payload.customlimit
        );
        yield put(listVehiclesSuccess({vehicles: data, save_as: action.payload.save_as}));
    } catch (error: any) {
        yield put(listVehiclesFailure(error.response?.data || 'Failed to list vehicles'));
    }
}

function* handleGetVehicle(action: ReturnType<typeof getVehicleRequest>) {
    try {
        const filters = action.payload.id ? 'id:' + action.payload.id : 'vin:' + action.payload.vin;
        const data: Vehicle[] = yield call(
            listVehicleApi,
            filters,
            action.payload.vehicle_fields,
            '',
        );
        yield put(getVehicleSuccess(data.length ? data[0] : {id: -1}));
    } catch (error: any) {
        yield put(getVehicleFailure(error.response?.data || 'Failed to list vehicles'));
    }
}

function* handleListVehicleDelivery(action: ReturnType<typeof listVehiclesDeliveryRequest>) {
    try {
        let filters = 'is_purchased:True,is_sold:False,status:pending_delivery,delivery_date:null';

        if (action.payload.date_from) {
            filters += ',delivery_date__date__gte:' + action.payload.date_from;
        }

        if (action.payload.date_to) {
            filters += ',delivery_date__date__lte:' + action.payload.date_to;
        }

        const data: Vehicle[] = yield call(
            listVehicleApi,
            filters,
            action.payload.vehicle_fields,
            'status:sold',
        );
        yield put(listVehiclesDeliverySuccess(data));
    } catch (error: any) {
        yield put(listVehiclesDeliveryFailure(error.response?.data || 'Failed to list vehicles'));
    }
}

function* handleUpdateVehicle(action: ReturnType<typeof updateVehicleRequest>) {
    try {
        const data: Vehicle = yield call(
            updateVehicleApi,
            action.payload.vehicle,
            action.payload.update_fields,
            action.payload.vehicle_fields,
        );
        if (action.payload.cb) {
            action.payload.cb(data);
        }
        yield put(updateVehicleSuccess(data));
    } catch (error: any) {
        yield put(updateVehicleFailure(error.response?.data || 'Failed to update vehicles'));
    }
}

function* handleGetMediaTypes() {
    try {
        const data: MediaType[] = yield call(getMediaTypesApi);
        yield put(getMediaTypesSuccess(data));
    } catch (error: any) {
        yield put(getMediaTypesFailure(error.response?.data || 'Failed to get media types'));
    }
}

function* handleUploadMedia(action: ReturnType<typeof uploadMediaRequest>) {
    try {
        const data: Media[] = yield call(uploadMediaApi, action.payload);
        yield put(uploadMediaSuccess({vehicle_id: action.payload.vehicle_id, medias: data}));
    } catch (error: any) {
        yield put(uploadMediaFailure(error.response?.data || 'Failed to upload media'));
    }
}

function* handleUploadDocument(action: ReturnType<typeof uploadDocumentRequest>) {
    try {
        const data: Document[] = yield call(uploadDocumentApi, action.payload);
        yield put(uploadDocumentSuccess({vehicle_id: action.payload.vehicle_id, documents: data}));
    } catch (error: any) {
        yield put(uploadDocumentFailure(error.response?.data || 'Failed to upload document'));
    }
}

function* handleGetInspections() {
    try {
        const data: InspectionSection[] = yield call(getInspectionsApi);
        yield put(getInspectionsSuccess(data));
    } catch (error: any) {
        yield put(getInspectionsFailure(error.response?.data || 'Failed to get inspections'));
    }
}

function* handleGetStoreLocations() {
    try {
        const data: Store[] = yield call(getStoreLocationsApi);
        yield put(getStoreLocationsSuccess(data));
    } catch (error: any) {
        yield put(getStoreLocationsFailure(error.response?.data || 'Failed to get store locations'));
    }
}

function* handleGetMakes() {
    try {
        const data: GetMakesResponse = yield call(getMakesApi);
        yield put(getMakesSuccess(data));
    } catch (error: any) {
        yield put(getMakesFailure(error.response?.data || 'Failed to get makes'));
    }
}

function* handleExportVehicles(action: ReturnType<typeof exportVehiclesRequest>) {
    try {
        const data: Blob = yield call(exportVehiclesApi, action.payload.ids);

        const link = document.createElement('a');
        link.target = '_blank'
        link.href = window.URL.createObjectURL(new Blob([data]));
        link.download = 'vehicles.csv';
        link.click();
    } catch (error: any) {
        // yield put(exportVehiclesFailure(error.response?.data || 'Failed to export vehicles'));
    }
}

function* handleUpdateMedia(action: ReturnType<typeof updateMediaRequest>) {
    try {
        yield call(updateMediaApi, action.payload);
        yield put(updateMediaSuccess({vehicle_id: action.payload.vehicle_id, media: action.payload.media}));
    } catch (error: any) {
        yield put(updateMediaFailure(error.response?.data || 'Failed to update media'));
    }
}

function* handleStartVehicleCheckout(action: ReturnType<typeof startVehicleCheckoutRequest>) {
    try {
        // @ts-ignore
        const data = yield call(startVehicleCheckoutApi, action.payload);
        if (action.payload.cb) {
            action.payload.cb(data);
        }
    } catch (error: any) {
        if (action.payload.cb) {
            action.payload.cb(null);
        }
    }
}

function* handleGetBodies() {
    try {
        const data: Body[] = yield call(getBodiesApi);
        yield put(getBodiesSuccess(data));
    } catch (error: any) {
        yield put(getBodiesFailure(error.response?.data || 'Failed to get bodies'));
    }
}

function* handleUploadBeforeAfterMedia(action: ReturnType<typeof uploadBeforeAfterMediaRequest>) {
    try {
        const data: BeforeAfterMedia = yield call(uploadBeforeAfterMediaApi, action.payload);
        yield put(uploadBeforeAfterMediaSuccess({vehicle_id: action.payload.vehicle_id, before_after: data}));
    } catch (error: any) {
        yield put(uploadBeforeAfterMediaFailure(error.response?.data || 'Failed to upload before after media'));
    }
}

function* handleGenBooklet(action: ReturnType<typeof genBookletRequest>) {
    try {
        const data: Vehicle = yield call(genBookletSummaryApi, action.payload);
        yield put(updateVehicleSuccess({id: action.payload.vehicle_id, ...data} as Vehicle));
    } catch (error: any) {
        yield put(updateVehicleFailure(error.response?.data || 'Failed to generate booklet'));
    }
}

function* handleGetBookletCarfax(action: ReturnType<typeof getBookletCarfaxRequest>) {
    try {
        const data: Vehicle = yield call(getBookletCarfaxOverviewApi, action.payload);
        yield put(updateVehicleSuccess({id: action.payload.vehicle_id, ...data}));
    } catch (error: any) {
        yield put(updateVehicleFailure(error.response?.data || 'Failed to get booklet carfax'));
    }
}

function* handleGenBookletFactoryBaseConfiguration(action: ReturnType<typeof genBookletFactoryBaseConfigurationRequest>) {
    try {
        const data: Vehicle = yield call(genBookletFactoryBaseConfigurationApi, action.payload);
        yield put(updateVehicleSuccess({id: action.payload.vehicle_id, ...data}));
    } catch (error: any) {
        yield put(updateVehicleFailure(error.response?.data || 'Failed to generate booklet factory base configuration'));
    }
}

function* handleGenBookletWhatWeDid(action: ReturnType<typeof genBookletWhatWeDidRequest>) {
    try {
        const data: Vehicle = yield call(genBookletWhatWeDidApi, action.payload);
        yield put(updateVehicleSuccess({id: action.payload.vehicle_id, ...data}));
    } catch (error: any) {
        yield put(updateVehicleFailure(error.response?.data || 'Failed to generate booklet what we did'));
    }
}

function* handleGenBookletNotes(action: ReturnType<typeof genBookletNotesRequest>) {
    try {
        const data: Vehicle = yield call(genBookletNotesApi, action.payload);
        yield put(updateVehicleSuccess({id: action.payload.vehicle_id, ...data}));
    } catch (error: any) {
        yield put(updateVehicleFailure(error.response?.data || 'Failed to generate booklet notes'));
    }
}

function* handleGenBookletWhatHappened(action: ReturnType<typeof genBookletWhatHappenedRequest>) {
    try {
        const data: Vehicle = yield call(genBookletWhatHappenedApi, action.payload);
        yield put(updateVehicleSuccess({id: action.payload.vehicle_id, ...data}));
    } catch (error: any) {
        yield put(updateVehicleFailure(error.response?.data || 'Failed to generate booklet what happened'));
    }
}

// Watcher sagas
export function* watchVehiclesSaga() {
    yield takeLatest(getVehicleRequest.type, handleGetVehicle);
    yield takeLatest(listVehiclesRequest.type, handleListVehicle);
    yield takeLatest(listVehiclesDeliveryRequest.type, handleListVehicleDelivery);
    yield takeLatest(updateVehicleRequest.type, handleUpdateVehicle);
    yield takeLatest(getMediaTypesRequest.type, handleGetMediaTypes);
    yield takeLatest(uploadMediaRequest.type, handleUploadMedia);
    yield takeLatest(uploadDocumentRequest.type, handleUploadDocument);
    yield takeLatest(getInspectionsRequest.type, handleGetInspections);
    yield takeLatest(getStoreLocationsRequest.type, handleGetStoreLocations);
    yield takeLatest(getMakesRequest.type, handleGetMakes);
    yield takeLatest(getBodiesRequest.type, handleGetBodies);
    yield takeLatest(exportVehiclesRequest.type, handleExportVehicles);
    yield takeLatest(updateMediaRequest.type, handleUpdateMedia);
    yield takeLatest(startVehicleCheckoutRequest.type, handleStartVehicleCheckout);
    yield takeLatest(uploadBeforeAfterMediaRequest.type, handleUploadBeforeAfterMedia);
    yield takeLatest(genBookletRequest.type, handleGenBooklet);
    yield takeLatest(getBookletCarfaxRequest.type, handleGetBookletCarfax);
    yield takeLatest(genBookletFactoryBaseConfigurationRequest.type, handleGenBookletFactoryBaseConfiguration);
    yield takeLatest(genBookletWhatWeDidRequest.type, handleGenBookletWhatWeDid);
    yield takeLatest(genBookletNotesRequest.type, handleGenBookletNotes);
    yield takeLatest(genBookletWhatHappenedRequest.type, handleGenBookletWhatHappened);
}